import { useQuery } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { MeetingSummary } from "../../shared/types/api";
import { Button, Card, CardHeader, EmptyState, ErrorState, LoadingState, StatusBadge } from "../../shared/ui";
import { ScheduleMeetingDrawer } from "./ScheduleMeetingDrawer";
import {
  PHASE_LABELS,
  PHASE_ORDER,
  PHASE_SUBTITLES,
  PHASE_TONE,
  formatMeetingTime,
  groupMeetings,
} from "./meetingVocabulary";

/** A project's meetings, led by the ones still owing minutes. */
export function ProjectMeetingsTab({ projectId }: { projectId: string }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [scheduling, setScheduling] = useState(false);
  const meetings = useQuery({
    queryKey: ["meetings", projectId],
    queryFn: () => api.get<MeetingSummary[]>(`/api/v1/projects/${projectId}/meetings`),
  });

  if (meetings.isLoading) {
    return <LoadingState />;
  }
  if (meetings.error) {
    return <ErrorState error={meetings.error} onRetry={() => void meetings.refetch()} />;
  }

  const all = meetings.data ?? [];
  const groups = groupMeetings(all);
  const open = (id: string) => navigate(`/projects/${projectId}/meetings/${id}`);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-ink-500">Decisions made in a room, written down where the project can find them.</p>
        {can("MEETING_MANAGE") && (
          <Button icon={<CalendarPlus className="h-4 w-4" />} onClick={() => setScheduling(true)}>
            Schedule meeting
          </Button>
        )}
      </div>

      {all.length === 0 ? (
        <EmptyState title="No meetings yet" message="Schedule one, and its minutes will live here once it has happened." />
      ) : (
        PHASE_ORDER.filter((phase) => groups[phase].length > 0).map((phase) => (
          <Card key={phase}>
            <CardHeader title={PHASE_LABELS[phase]} subtitle={PHASE_SUBTITLES[phase]} />
            <ul>
              {groups[phase].map((meeting) => (
                <li key={meeting.id}>
                  <button
                    type="button"
                    onClick={() => open(meeting.id)}
                    className="flex w-full items-center gap-4 border-b border-ink-100 px-5 py-3 text-left last:border-0 hover:bg-ink-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink-900">{meeting.title}</p>
                      <p className="text-xs text-ink-500">
                        {formatMeetingTime(meeting.startsAt, meeting.endsAt)} · {meeting.organiser.fullName}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-ink-500">
                      {meeting.attendeeCount} {meeting.attendeeCount === 1 ? "person" : "people"}
                    </span>
                    {phase === "COMPLETED" && meeting.openActions > 0 && (
                      <StatusBadge tone="amber">
                        {meeting.openActions} open {meeting.openActions === 1 ? "action" : "actions"}
                      </StatusBadge>
                    )}
                    <StatusBadge tone={PHASE_TONE[phase]}>{PHASE_LABELS[phase]}</StatusBadge>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        ))
      )}

      <ScheduleMeetingDrawer
        projectId={projectId}
        open={scheduling}
        onClose={() => setScheduling(false)}
        onSaved={(saved) => open(saved.id)}
      />
    </div>
  );
}
